/**
 * feat-270 — one lesson card in the approval queue.
 *
 * `design/approval-queue.html` states A, F, I and J.
 *
 * THE CHIP RULE (NOTES §2, §3), which is the thing most likely to be "improved":
 * exactly ONE state chip is drawn, and **pending is NEUTRAL while never-approved
 * spends the colour.** That is not a taste call — `QuizContentManagement.tsx`
 * ruled this exact question for the History diff in 2026-07-18 and the reasoning
 * transfers word for word: a changed field in a routine diff is not a caution,
 * and it is FREQUENT rather than rare, so amber would dilute the real caution
 * signal and read alarming when many rows light at once. `SaveStatus.tsx` names
 * the cost directly — *"colour in this footer means 'something needs you'.
 * Spending it on the outcome that fires fifty times an hour leaves nothing for
 * the one that doesn't."*
 *
 * So the amber is saved for the state that is rare, surprising, and has a
 * consequence nobody guesses: **content that is switched on and reaches nobody.**
 *
 * **Never-approved supersedes pending.** A lesson with no approved snapshot has
 * nothing to be pending *against* — every item under it is unapproved by
 * definition. One chip, never two.
 */

import React from 'react';
import { APPROVAL_QUEUE_COPY as C } from '../approvalCopy';
import { ApprovalItemDiff, ApprovalItemHeader } from './ApprovalItemDiff';
import { timeAgo } from '../relativeTime';
import {
  actorDisplayName,
  type ApprovalLessonDetail,
  type ApprovalPendingItem,
} from '../../../../services/curriculumApprovalService';

export interface ApprovalLessonCardProps {
  lesson: ApprovalLessonDetail;
  /** ADR-B §5: a RENDERING hint, not the guard. The guard is the 403 (AT-19). */
  canApprove: boolean;
  selected: boolean;
  onToggleSelected: () => void;
  expanded: boolean;
  onToggleExpanded: () => void;
  /** This card was redrawn from a 409 body (state I). */
  conflicted: boolean;
  onApprove: () => void;
  approving: boolean;
  onRestore: (historyId: string) => void;
}

export const ApprovalLessonCard: React.FC<ApprovalLessonCardProps> = ({
  lesson,
  canApprove,
  selected,
  onToggleSelected,
  expanded,
  onToggleExpanded,
  conflicted,
  onApprove,
  approving,
  onRestore,
}) => {
  const neverApproved = lesson.state === 'never_approved';
  const actor = lesson.last_change_by ?? lesson.approved_by;
  const when = lesson.last_changed_at ?? lesson.approved_at;

  const hidden = lesson.pending_item_count - lesson.items_returned;

  return (
    <div
      className={`mb-4 bg-white border rounded-2xl shadow-sm overflow-hidden ${
        conflicted ? 'border-attention-line/50' : 'border-softBorder'
      }`}
      data-testid={`approval-lesson-${lesson.lesson_id}`}
    >
      <div className="flex items-center gap-2.5 px-4 py-3">
        {/*
          AT-28 / NOTES §4: for a store manager the checkbox is ABSENT, not
          disabled. `components.md`'s gated-capability rule — a precondition the
          reader CANNOT satisfy is a plain statement with no control — and eight
          dead checkboxes is the noise case it warns about. The one disabled
          control lives in the bulk bar, once, explained.
        */}
        {canApprove && (
          <input
            type="checkbox"
            className="w-3.5 h-3.5 accent-coral shrink-0"
            checked={selected}
            onChange={onToggleSelected}
            aria-label={`Select ${lesson.title}`}
          />
        )}
        <button
          type="button"
          onClick={onToggleExpanded}
          aria-expanded={expanded}
          aria-label={expanded ? C.collapse(lesson.title) : C.expand(lesson.title)}
          className="text-inkMuted text-xs w-3 shrink-0"
        >
          {expanded ? '▾' : '▸'}
        </button>
        {lesson.emoji && (
          <span className="text-base leading-none shrink-0" aria-hidden>
            {lesson.emoji}
          </span>
        )}
        {/* The title is the only thing that truncates; everything right of it is
            `shrink-0` (NOTES, state E). */}
        <span className="font-semibold min-w-0 truncate">{lesson.title}</span>

        {/* ── exactly ONE chip ─────────────────────────────────────────────── */}
        {conflicted ? (
          <span className="bg-attention-surface text-attention-on text-[10px] font-bold rounded-full px-2 py-0.5 shrink-0 inline-flex items-center gap-1">
            <span aria-hidden>⚠</span> {C.changedSinceOpenedChip}
          </span>
        ) : neverApproved ? (
          // The ONE state that earns colour. `attention-*` chosen explicitly over
          // `dash-gold-on` per TOKENS §1o rule 3, and paired with a glyph AND a
          // sentence per rule 2 — never told by hue alone.
          <span
            className="bg-attention-surface text-attention-on text-[10px] font-bold rounded-full px-2 py-0.5 shrink-0 inline-flex items-center gap-1"
            title={C.neverApprovedChipLabel}
          >
            <span aria-hidden>⚠</span> {C.neverApprovedChip}
          </span>
        ) : (
          // Pending is NEUTRAL. `●` is `ChangedDot`'s glyph, which is what ties
          // this chip to the diff vocabulary in the queue and the History panel.
          <span
            className="bg-cream-dark text-inkMuted text-[10px] font-bold rounded-full px-1.5 py-0.5 shrink-0"
            title={C.pendingChipLabel(lesson.pending_item_count)}
          >
            ● {lesson.pending_item_count}
          </span>
        )}

        {actor && when && (
          <span className="ml-auto shrink-0 text-xs text-inkMuted truncate max-w-[15rem]" title={actor.email}>
            {neverApproved
              ? C.createdBy(actorDisplayName(actor), timeAgo(when))
              : C.lastChangeBy(actorDisplayName(actor), timeAgo(when))}
          </span>
        )}

        {canApprove && (
          <button
            type="button"
            data-testid={`approve-lesson-${lesson.lesson_id}`}
            onClick={onApprove}
            disabled={approving}
            className="shrink-0 text-xs font-bold rounded-full px-2.5 py-1 border border-softBorder bg-white text-ink hover:bg-cream disabled:opacity-50"
          >
            {C.approve}
          </button>
        )}
      </div>

      {/*
        The never-approved sentence. Copy names the CONSEQUENCE, not the status —
        "never approved" is jargon; "employees cannot see it" is the thing the
        reader is actually confused about.
      */}
      {expanded && neverApproved && (
        <div className="mx-4 mb-3 rounded-xl bg-attention-surface text-attention-on border border-attention-line/50 px-3 py-2 text-[11.5px] leading-snug">
          <strong className="font-bold">{C.neverApprovedPanelLead}</strong>{' '}
          {C.neverApprovedPanelRest}
        </div>
      )}

      {/*
        State I's panel line. It leads with the NEW count against the one the
        reader had, because "4 changes now, not 3" is the fact that makes the
        situation legible without arithmetic.
      */}
      {expanded && conflicted && (
        <div className="mx-4 mb-3 rounded-xl bg-attention-surface text-attention-on border border-attention-line/50 px-3 py-2 text-[11.5px] leading-snug">
          <strong className="font-bold">{C.conflictPanelLead(lesson.pending_item_count, newCount(lesson))}</strong>{' '}
          <span className="font-bold">{C.newSinceOpened}</span> {C.conflictPanelRest}
        </div>
      )}

      {expanded && (
        <div className="border-t border-softBorder">
          {lesson.items.map((item) => (
            <div
              key={item.entity_ref.entity_id}
              className="px-4 py-3 border-b border-softBorder last:border-b-0"
            >
              <ApprovalItemHeader item={item} />
              <ApprovalItemDiff
                item={item}
                onRestore={
                  item.history_id ? () => onRestore(item.history_id as string) : undefined
                }
              />
            </div>
          ))}
          {/* `pending_item_count` is exact while `items` is capped at
              `item_limit` (ADR-B §3.2), so this is the "+ 6 more … Show all 8"
              line. It reads the two server counts and subtracts them; it does not
              count anything itself. */}
          {hidden > 0 && (
            <div className="px-4 py-2.5 text-xs text-inkMuted">
              {C.moreItems(hidden)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * How many of this card's items the reader had already seen — the "not 3" half of
 * "4 changes now, not 3".
 *
 * Derived from the SERVER's `is_new_since_review`: items explicitly marked `false`
 * are the ones present with an identical digest, i.e. the ones already reviewed.
 * `null` is not counted, because on a plain read the question has no referent.
 */
function newCount(lesson: ApprovalLessonDetail): number {
  return lesson.items.filter((i: ApprovalPendingItem) => i.is_new_since_review === false)
    .length;
}
